import {
  Users,
  Calendar,
  DollarSign,
  Bell,
  Shield,
  Cloud,
  Zap,
  CheckCircle2,
  TrendingUp,
  Clock,
  FileText,
  Settings,
  MessageSquare,
  Database,
  Lock,
  Smartphone,
  Globe,
  Award,
  Target,
  UserCheck,
  Briefcase,
  Building2,
  BarChart3,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  Heart,
  Star,
} from "lucide-react";

// Hero Section Data
export const heroSection = {
  badge: "Complete Human Resource Management Platform",
  title: "HRM",
  highlightedText: "Transform Your Staff Operations",
  description:
    "A comprehensive, cloud-based Human Resource Management platform built specifically for educational institutions. Simplify staff records, attendance tracking, payroll processing, and leave management. Empower your HR team with cutting-edge technology that streamlines operations and enhances productivity.",
  cta: {
    primary: "Schedule Demo",
    secondary: "Explore Features",
  },
  stats: [
    { value: "300+", label: "Institutions Trust Us" },
    { value: "50K+", label: "Staff Managed" },
    { value: "99.9%", label: "Uptime Guarantee" },
    { value: "24/7", label: "HR Support" },
  ],
};

// Challenges Section
export const challenges = {
  title: "The Challenges",
  highlightedText: "Institutions Face",
  description:
    "Managing human resources manually leads to errors, time loss, and communication gaps. Educational institutions struggle with fragmented systems, repetitive admin tasks, and inefficient workflows — all of which lower productivity and increase frustration.",
  cards: [
    {
      icon: FileText,
      title: "Manual Record Management",
      description:
        "Still using spreadsheets or paper files for staff records? These outdated methods lead to errors, data loss, and time-consuming processes. Manual entry increases mistakes, especially during payroll, audits, or reporting.",
      gradient: "from-red-500/10 to-orange-500/10",
      iconColor: "text-red-500",
    },
    {
      icon: Database,
      title: "Lack of Unified HR Platform",
      description:
        "When data is spread across multiple tools or files, HR tasks become chaotic. From leave balances to salary records, staff and admin must switch between systems, wasting valuable time and leading to poor decision-making.",
      gradient: "from-blue-500/10 to-cyan-500/10",
      iconColor: "text-blue-500",
    },
    {
      icon: Settings,
      title: "Disjointed HR Processes",
      description:
        "Without a centralized HR system, tasks like onboarding, approvals, and communication are delayed. Important updates can be missed, staff becomes disengaged, and the entire HR workflow suffers inefficiencies.",
      gradient: "from-purple-500/10 to-pink-500/10",
      iconColor: "text-purple-500",
    },
  ],
};

// Solution Features
export const solutionFeatures = {
  title: "Why Choose",
  highlightedText: "HRM",
  description:
    "HRM empowers educational institutions with a robust HR platform to manage all staff-related operations from a single place. Improve transparency, efficiency, and employee satisfaction by digitizing your HR processes.",
  features: [
    {
      icon: Cloud,
      title: "Cloud-Based & Real-Time",
      description:
        "Access staff data, leave records, and payroll processing from anywhere, anytime — with secure cloud hosting and live updates. No installation required, automatic backups, and seamless synchronization across devices.",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-500",
    },
    {
      icon: Zap,
      title: "All-in-One HR Solution",
      description:
        "Say goodbye to spreadsheets—manage attendance, payroll, leave, and holidays all in one unified HRM system. Streamline workflows, reduce errors, and save valuable time with integrated modules.",
      gradient: "from-yellow-500/20 to-orange-500/20",
      iconColor: "text-yellow-500",
    },
    {
      icon: Shield,
      title: "Highly Secure and Compliant",
      description:
        "We follow best practices in data security and offer encrypted access, role-level permissions, and regular backups to keep your HR operations safe. GDPR compliant with audit trails and data protection.",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-500",
    },
  ],
};

// Module Features
export const moduleFeatures = {
  title: "Explore Our Powerful",
  highlightedText: "Module Features",
  description:
    "Comprehensive HR modules designed to handle every aspect of staff management efficiently and effectively.",
  modules: [
    {
      id: "staff-management",
      title: "Staff Management",
      icon: Users,
      description:
        "Organize and control your entire staff database with ease. Centralized system for all employee information.",
      color: "text-purple-500",
      bgGradient: "from-purple-500/10 to-pink-500/10",
      features: [
        "Add, update, or remove staff details anytime with bulk import options",
        "Assign roles to monitor attendance, payroll, and permissions",
        "Manage departments, designations, and role hierarchies effortlessly",
        "Track employee history, documents, and certifications",
      ],
      details:
        "A centralized system to streamline your staff operations from top to bottom. Store comprehensive employee profiles, manage organizational structure, and maintain complete staff records in one secure platform.",
    },
    {
      id: "attendance-leaves",
      title: "Attendance & Leaves",
      icon: Calendar,
      description:
        "Smarter tracking of daily attendance and leave—automated and reliable. Real-time monitoring and approval workflows.",
      color: "text-green-500",
      bgGradient: "from-green-500/10 to-emerald-500/10",
      features: [
        "Enable location-based, one-click attendance for secure time tracking",
        "Auto-mark absents, leaves, and holidays—no manual effort needed",
        "Staff can apply for leave; admins approve with updates reflected instantly",
        "View attendance reports, patterns, and analytics",
      ],
      details:
        "Simplified attendance and leave management that saves time and boosts accuracy. Automated tracking, flexible leave policies, and comprehensive reporting help you maintain perfect attendance records.",
    },
    {
      id: "payroll",
      title: "Payroll Management",
      icon: DollarSign,
      description:
        "Handle payroll generation without the usual headache. Automated calculations with complete transparency.",
      color: "text-orange-500",
      bgGradient: "from-orange-500/10 to-amber-500/10",
      features: [
        "Auto-generate or manually manage payrolls—no spreadsheet juggling",
        "Track work hours, leaves, and deductions all in one place",
        "Staff can view their payslips; admins can release and approve with ease",
        "Support for multiple salary structures and payment methods",
      ],
      details:
        "A smooth, transparent payroll process—for both staff and admins. Automated salary calculations, tax deductions, bonuses, and comprehensive payroll reports ensure accuracy and compliance.",
    },
    {
      id: "smart-management",
      title: "Smart Management",
      icon: Smartphone,
      description:
        "Seamlessly manage your institution through our intuitive web and mobile platforms, built for convenience and flexibility.",
      color: "text-blue-500",
      bgGradient: "from-blue-500/10 to-indigo-500/10",
      features: [
        "Accessible via both web and app — manage anytime, anywhere",
        "User-friendly interface — clean design for effortless navigation",
        "Custom layouts — personalize your workspace to suit your needs",
        "Offline mode support for mobile app",
      ],
      details:
        "Our system empowers institutions to manage operations from both web and mobile platforms. With options like mini view, RTL support, and dark mode, you can work comfortably wherever you are—making operations smoother and more flexible.",
    },
    {
      id: "notifications",
      title: "Smart Notifications",
      icon: Bell,
      description:
        "Keep your staff informed at every step—automatically. Real-time alerts and communication tools.",
      color: "text-cyan-500",
      bgGradient: "from-cyan-500/10 to-blue-500/10",
      features: [
        "Send real-time updates for attendance, leave approvals, and payroll status",
        "Notify staff of holidays, announcements, and important events",
        "Reduce miscommunication with timely and relevant alerts",
        "Email, SMS, and in-app notification support",
      ],
      details:
        "Automated communication that keeps everyone in the loop—effortlessly. Customizable notification preferences, bulk messaging, and scheduled announcements ensure effective communication across your institution.",
    },
    {
      id: "reports",
      title: "Reports & Analytics",
      icon: BarChart3,
      description:
        "Get clear insights to support smarter HR decisions. Comprehensive reporting and analytics dashboard.",
      color: "text-teal-500",
      bgGradient: "from-teal-500/10 to-cyan-500/10",
      features: [
        "Attendance reports with patterns and trends analysis",
        "Payroll reports with cost breakdowns and summaries",
        "Leave balance reports and utilization statistics",
        "Custom report builder for specific requirements",
      ],
      details:
        "Powerful reporting tools that help you make data-driven decisions. Generate comprehensive HR reports, analyze trends, track KPIs, and export data in multiple formats for further analysis.",
    },
    {
      id: "user-management",
      title: "User Management",
      icon: Settings,
      description:
        "Easily manage user accounts, access, and security in one place. Role-based access control system.",
      color: "text-indigo-500",
      bgGradient: "from-indigo-500/10 to-purple-500/10",
      features: [
        "Manage Accounts: Create, update, and view user profiles",
        "Roles & Permissions: Set role-based access for better control",
        "Secure Login: Enable safe sign-in with multi-factor authentication",
        "Activity logs and audit trails",
      ],
      details:
        "HRM's user management helps you control who can access what, keeping your platform secure and organized. From handling account requests to defining permissions, everything is simple, safe, and efficient.",
    },
    {
      id: "support",
      title: "Support & Help",
      icon: MessageSquare,
      description:
        "Reliable support system to resolve issues and improve user satisfaction. Multiple support channels available.",
      color: "text-pink-500",
      bgGradient: "from-pink-500/10 to-rose-500/10",
      features: [
        "Messages: Centralized platform for queries and suggestions",
        "Ticketing: Track and resolve issues efficiently",
        "Live Assistance: Continuous support to keep operations running smoothly",
        "Knowledge base and video tutorials",
      ],
      details:
        "We're here when you need us. Our support system ensures fast responses, clear communication, and smooth resolution of any challenges you may encounter. Dedicated support channels for different priority levels.",
    },
  ],
};

// Roles Section
export const roles = {
  title: "Designed to Help",
  highlightedText: "Every Stakeholder",
  description:
    "HRM is designed to empower every stakeholder in your institution with the tools they need to manage HR operations efficiently.",
  roles: [
    {
      icon: Briefcase,
      title: "HR Managers",
      description:
        "Gain full control over staff operations, from hiring and onboarding to attendance and payroll. Eliminate paperwork and streamline your HR efforts from one platform.",
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-500",
    },
    {
      icon: Users,
      title: "Teachers & Staff",
      description:
        "Access your attendance records, leaves, salary slips, and notifications in one secure portal. Stay informed and connected with real-time updates.",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-500",
    },
    {
      icon: Building2,
      title: "Administrators",
      description:
        "Get centralized insights into department-level staff data, approvals, and performance. Reduce delays in decision-making with accurate HR reports.",
      gradient: "from-purple-500/20 to-pink-500/20",
      iconColor: "text-purple-500",
    },
    {
      icon: Award,
      title: "Directors & Owners",
      description:
        "Access powerful reports and metrics on staff productivity, costs, and resource allocation. Ensure your institution runs efficiently with full visibility.",
      gradient: "from-orange-500/20 to-red-500/20",
      iconColor: "text-orange-500",
    },
  ],
};

// Pricing Plans
export const pricingPlans = {
  title: "Our",
  highlightedText: "Pricing Plans",
  description:
    "Flexible pricing designed to suit institutions of any size. Only pay for what you need, and scale as your team grows.",
  plans: [
    {
      name: "Basic",
      price: "Free",
      period: "forever",
      description: "Perfect for small institutions getting started",
      features: [
        "Up to 50 staff members",
        "Basic staff record management",
        "Leave tracking",
        "Simple payroll processing",
        "Email notifications",
        "Email support",
      ],
      cta: "Get Started",
      popular: false,
      gradient: "from-gray-500/10 to-gray-600/10",
    },
    {
      name: "Professional",
      price: "₹19,999",
      period: "per year",
      description: "Ideal for growing institutions",
      features: [
        "Up to 200 staff members",
        "All Basic features",
        "Advanced attendance system",
        "Multi-role access control",
        "Custom HR reports & analytics",
        "Priority support",
        "Training sessions",
      ],
      cta: "Start Free Trial",
      popular: true,
      gradient: "from-primary/20 to-primary/10",
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large institutions and multi-campus",
      features: [
        "Unlimited staff members",
        "All Professional features",
        "Multi-campus management",
        "Integration with ERP/CRM",
        "Dedicated account manager",
        "24/7 premium support",
        "Custom development",
        "API access",
      ],
      cta: "Contact Sales",
      popular: false,
      gradient: "from-purple-500/20 to-pink-500/20",
    },
  ],
};

// Registration Steps
export const registrationSteps = {
  title: "How to",
  highlightedText: "Get Started",
  description:
    "Get started with HRM in just a few simple steps. Our streamlined onboarding process ensures you're up and running quickly.",
  steps: [
    {
      number: "01",
      title: "Register Your Institution",
      description:
        "Create an account by registering your institution details and adding your HR contacts. Our team will verify your details and activate your account within 24 hours.",
      icon: UserCheck,
    },
    {
      number: "02",
      title: "Add Staff Information",
      description:
        "Import or manually enter your team members' data including personal info, roles, and departments. Our bulk import feature saves you time and effort.",
      icon: Database,
    },
    {
      number: "03",
      title: "Configure Attendance & Payroll",
      description:
        "Set up work hours, leave types, holidays, and payroll configurations according to your HR policies. Our guided setup wizard makes this process simple.",
      icon: Settings,
    },
    {
      number: "04",
      title: "Start Managing HR Operations",
      description:
        "Use the dashboard to monitor attendance, approve leaves, run payroll, and access reports with ease. Our support team is available to help you get started.",
      icon: CheckCircle2,
    },
  ],
};

// FAQs
export const faqs = {
  title: "Frequently Asked",
  highlightedText: "Questions",
  description:
    "Have questions about how HRM can help your institution? We've compiled answers to help you get started and feel confident with our platform.",
  questions: [
    {
      question: "Is HRM suitable for small institutions?",
      answer:
        "Absolutely! Whether you have a small team or multiple campuses, HRM adapts to your needs with scalable tools and user roles. Our Basic plan is free and perfect for small institutions getting started with HR management.",
    },
    {
      question: "Can we migrate existing staff records into HRM?",
      answer:
        "Yes, we offer seamless data import assistance from Excel, CSV, or previous HR software platforms. Our support team will guide you through the migration process and ensure all your data is transferred accurately and securely.",
    },
    {
      question: "Does HRM support multiple user roles?",
      answer:
        "Yes. HRM allows administrators, HR staff, and employees to have distinct access levels and dashboards, ensuring secure data control. You can customize permissions for each role based on your institution's requirements.",
    },
    {
      question: "How secure is the data in HRM?",
      answer:
        "We use top-grade encryption (SSL/TLS), multi-factor authentication, and role-based access to ensure your staff information remains confidential and protected. We comply with data protection regulations and conduct regular security audits.",
    },
    {
      question: "Is training available for our HR team?",
      answer:
        "Yes, we provide onboarding sessions, detailed guides, video tutorials, and ongoing support to ensure your team is confident using the platform. Professional and Enterprise plans include dedicated training sessions.",
    },
    {
      question: "Can I try HRM before purchasing?",
      answer:
        "We offer personalized demos where you can explore key features. Contact us to schedule your session. We also provide a free Basic plan and a 30-day free trial of our Professional plan for larger institutions.",
    },
    {
      question: "Does HRM integrate with other systems?",
      answer:
        "Yes, HRM offers API access and can integrate with various third-party systems including accounting software, payroll services, and other educational management systems. Enterprise plans include custom integration services.",
    },
    {
      question: "What kind of support do you provide?",
      answer:
        "We offer multiple support channels including email support, phone support, live chat, and a comprehensive knowledge base. Professional and Enterprise plans include priority support with faster response times. Enterprise customers also get a dedicated account manager and 24/7 support access.",
    },
  ],
};

// Benefits/Stats Section
export const benefits = {
  title: "Why Institutions Choose",
  highlightedText: "HRM",
  stats: [
    {
      icon: TrendingUp,
      value: "75%",
      label: "Time Saved on HR Tasks",
      description: "Automate repetitive tasks and focus on strategy",
      color: "text-green-500",
    },
    {
      icon: Award,
      value: "300+",
      label: "Institutions Trust Us",
      description: "Join hundreds of satisfied organizations",
      color: "text-blue-500",
    },
    {
      icon: Target,
      value: "99.9%",
      label: "Uptime Guarantee",
      description: "Reliable service you can count on",
      color: "text-purple-500",
    },
    {
      icon: Clock,
      value: "24/7",
      label: "Support Available",
      description: "Get help whenever you need it",
      color: "text-orange-500",
    },
  ],
};
